import request from '@/utils/request'

/**
 * 获取材料库存列表
 * @param {Object} params - 查询参数
 * @returns {Promise}
 */
export const getMaterialInventory = (params) => {
  return request({
    url: '/api/material-inventory',
    method: 'get',
    params,
    // 按料号分页后通常数秒内返回；保留余量给复杂筛选
    timeout: 45000
  })
}

/**
 * 创建材料库存记录
 * @param {Object} data - 库存数据
 * @returns {Promise}
 */
export const createMaterialInventory = (data) => {
  return request({
    url: '/api/material-inventory',
    method: 'post',
    data
  })
}

/**
 * 获取材料库存记录详情
 * @param {number} inventoryId - 库存记录ID
 * @returns {Promise}
 */
export const getMaterialInventoryRecord = (inventoryId) => {
  return request({
    url: `/api/material-inventory/${inventoryId}`,
    method: 'get'
  })
}

/**
 * 料号维度库存交易记录（默认含全部库位，与库位筛选无关）
 */
export const getMaterialInventoryBatchTransactions = (batchId, params) => {
  return request({
    url: `/api/material-inventory/batches/${batchId}/transactions`,
    method: 'get',
    params: { scope: 'batch', ...params }
  })
}

/**
 * 库存交易记录（默认 scope=batch：料号全部交易；scope=slot 仅本库位）
 * 需后端管理员/超级用户权限
 */
export const getMaterialInventorySlotTransactions = (inventoryId, params) => {
  return request({
    url: `/api/material-inventory/${inventoryId}/slot-transactions`,
    method: 'get',
    params: { scope: 'batch', red_flush_filter: 'exclude', ...params }
  })
}

/**
 * 更新材料库存记录
 * @param {number} inventoryId - 库存记录ID
 * @param {Object} data - 更新数据
 * @returns {Promise}
 */
export const updateMaterialInventory = (inventoryId, data) => {
  return request({
    url: `/api/material-inventory/${inventoryId}`,
    method: 'put',
    data
  })
}

/** 标记/取消「跑码实数」（数量旁红字「实」） body: { marked: boolean } */
export const patchMaterialInventoryMeterActualMark = (inventoryId, data) => {
  return request({
    url: `/api/material-inventory/${inventoryId}/meter-actual-mark`,
    method: 'patch',
    data,
    timeout: 60000
  })
}

/** 标记/取消「色卡」（系统料号旁红字「色卡」） body: { marked: boolean } */
export const patchMaterialInventoryColorCardMark = (inventoryId, data) => {
  return request({
    url: `/api/material-inventory/${inventoryId}/color-card-mark`,
    method: 'patch',
    data,
    timeout: 60000
  })
}

/** 按仓库料号批量标记色卡 body: { text } 或 { codes: [] }，可选 marked */
export const batchMaterialInventoryColorCardMark = (data) => {
  return request({
    url: `/api/material-inventory/color-card-mark/batch`,
    method: 'post',
    data,
    timeout: 120000
  })
}

/** 色卡批量未匹配待补清单 */
export const listColorCardPending = () => {
  return request({
    url: `/api/material-inventory/color-card-mark/pending`,
    method: 'get',
    timeout: 60000
  })
}

/** 忽略待补项 body: { ids: [] } 或 { codes: [] } */
export const dismissColorCardPending = (data) => {
  return request({
    url: `/api/material-inventory/color-card-mark/pending/dismiss`,
    method: 'post',
    data,
    timeout: 60000
  })
}

/** 对待补清单重新匹配 body 可选 { codes, ids, marked } */
export const rematchColorCardPending = (data = {}) => {
  return request({
    url: `/api/material-inventory/color-card-mark/pending/rematch`,
    method: 'post',
    data,
    timeout: 120000
  })
}

/** 库位调整（写调拨交易记录 + 更新库存行库位） */
export const transferMaterialInventoryLocation = (inventoryId, data) => {
  return request({
    url: `/api/material-inventory/${inventoryId}/transfer-location`,
    method: 'post',
    data
  })
}

/**
 * 删除材料库存记录
 * @param {number} inventoryId - 库存记录ID
 * @returns {Promise}
 */
export const deleteMaterialInventory = (inventoryId) => {
  return request({
    url: `/api/material-inventory/${inventoryId}`,
    method: 'delete'
  })
}

/**
 * 获取材料库存汇总统计
 * @param {Object} params - 查询参数
 * @returns {Promise}
 */
export const getMaterialInventorySummary = (params) => {
  return request({
    url: '/api/material-inventory/summary',
    method: 'get',
    params
  })
}

/**
 * 获取库存统计信息
 * @param {Object} params - 查询参数（筛选条件）
 * @returns {Promise}
 */
export const getInventoryStats = (params = {}) => {
  return request({
    url: '/api/material-inventory/summary',
    method: 'get',
    params,
    timeout: 60000
  })
}

/**
 * 获取材料分支列表（已移除 - 材料分支功能不再使用）
 * @deprecated 材料分支功能已移除
 */
export const getMaterialBranches = () => {
  // 返回空数组，保持API兼容性
  return Promise.resolve({ data: [] })
}

/**
 * 同步启用的材料身份到库存
 * @returns {Promise}
 */
export const syncEnabledMaterialIdentities = () => {
  return request({
    url: '/api/material-inventory/sync-enabled-identities',
    method: 'post'
  })
}

/**
 * 导出材料库存Excel
 * @returns {Promise}
 */
export const exportMaterialInventoryExcel = () => {
  return request({
    url: '/api/material-inventory/export-excel',
    method: 'get'
  })
}

/**
 * 导入材料库存Excel
 * @param {FormData} formData - 表单数据
 * @returns {Promise}
 */
export const importMaterialInventoryExcel = (formData) => {
  return request({
    url: '/api/material-inventory/import-excel',
    method: 'post',
    data: formData,
    headers: {
      'Content-Type': 'multipart/form-data'
    }
  })
}